import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { DefaultAndAuthProps } from "model/props";
import PageHeader from "components/pageheader/PageHeader";
import ConfirmDialog from "components/confirmdialog/ConfirmDialog";
import FormButton from "components/form/FormButton";
import TextInput from "components/form/TextInput";
import { Box, Typography, Snackbar, Alert } from "@mui/material";
import cssLayout from "../layout.module.scss";
import css from "./users2screen.module.scss";
import { getLanguageString as _ } from "misc/lang";
import { Paths } from "appConfig";
import { apiUserDelete, apiUserUpdate, apiGetUserList } from "net/requests";
import { User } from "model/user";

interface UserFormData {
  identifier: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  location: string;
}

const EditUserScreen: React.FC<DefaultAndAuthProps> = (props) => {
  const { account, home, admin, loader, navigate } = props;
  const { id } = useParams();
  const location = useLocation();
  const initialUser = (location.state as { user?: User })?.user;
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [formData, setFormData] = useState<UserFormData>({
    identifier: "",
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    location: "",
  });
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const orgName = home?.name || account?.name || "";
  const title = `${_("user", "edit_title_prefix")}${orgName}`;
  const subtitle = _("user", "edit_subtitle");

  useEffect(() => {
    // Prefer data from navigation state for instant load
    if (initialUser) {
      setFormData((prev) => ({
        ...prev,
        identifier: initialUser.identifier || "",
      }));
      return;
    }

    // Fallback to fetching the list and selecting by id
    if (!home || !id || !admin?.access_token) return;
    apiGetUserList(admin, home.uuid)
      .then((response) => {
        const users: User[] = response.data;
        const user = users.find((u) => u.uuid === id);
        if (user) {
          setFormData((prev) => ({
            ...prev,
            identifier: user.identifier || "",
          }));
        }
      })
      .catch((error) => {
        console.error("Error loading user:", error);
      });
  }, [admin, home, id, initialUser]);

  const handleDelete = () => setDeleteDialog(true);

  const handleSave = async () => {
    if (!id) {
      setNotification({
        open: true,
        message: _("user", "error_no_user"),
        severity: "error",
      });
      return;
    }

    if (!formData.identifier.trim()) {
      setNotification({
        open: true,
        message: _("user", "error_identifier_required"),
        severity: "error",
      });
      return;
    }

    try {
      loader.setLoading();
      const payload = {
        identifier: formData.identifier.trim(),
      };
      await apiUserUpdate(admin, id, payload);
      loader.reset();
      setNotification({
        open: true,
        message: _("user", "update_success"),
        severity: "success",
      });
      setTimeout(() => {
        navigate(Paths.USERS2);
      }, 1000);
    } catch (error: any) {
      loader.reset();
      const errorCode = error?.response?.data?.error;
      console.error("Error updating user:", errorCode);
      setNotification({
        open: true,
        message: _("user", "update_error"),
        severity: "error",
      });
    }
  };

  const handleCancel = () => {
    navigate(Paths.USERS2);
  };

  const confirmDelete = async () => {
    if (!admin || !id) return;
    try {
      loader.setLoading();
      await apiUserDelete(admin, id);
      loader.reset();
      setDeleteDialog(false);
      setNotification({
        open: true,
        message: _("user", "delete_success"),
        severity: "success",
      });
      setTimeout(() => {
        navigate(Paths.USERS2);
      }, 1000);
    } catch (error) {
      loader.reset();
      console.error("Error deleting user:", error);
      setNotification({
        open: true,
        message: _("user", "delete_error"),
        severity: "error",
      });
      setDeleteDialog(false);
    }
  };

  return (
    <div className={cssLayout.layout}>
      <PageHeader
        title={title}
        subtitle={subtitle}
        showDeleteButton={true}
        onDelete={handleDelete}
        deleteButtonText={_("organisation", "delete_button")}
      />

      <div className={css.contentBox}>
        {/* Basic Information Section */}
        <Box className={css.section}>
          <Typography variant="h6" className={css.sectionTitle}>
            {_("user", "basic_information")}
          </Typography>

          <Box className={css.formGrid}>
            <TextInput
              label={_("user", "identifier_label")}
              placeholder={_("user", "placeholder_identifier")}
              labelOutside={true}
              isRequired={true}
              initialValue={formData.identifier}
              updateValue={(value) =>
                setFormData((prev) => ({ ...prev, identifier: value }))
              }
            />

            <TextInput
              label={_("user", "first_name_label")}
              placeholder={_("user", "placeholder_first_name")}
              labelOutside={true}
              initialValue={formData.firstName}
              updateValue={(value) =>
                setFormData((prev) => ({ ...prev, firstName: value }))
              }
            />

            <TextInput
              label={_("user", "last_name_label")}
              placeholder={_("user", "placeholder_last_name")}
              labelOutside={true}
              initialValue={formData.lastName}
              updateValue={(value) =>
                setFormData((prev) => ({ ...prev, lastName: value }))
              }
            />

            <TextInput
              label={_("user", "email_label")}
              placeholder={_("user", "placeholder_email")}
              labelOutside={true}
              initialValue={formData.email}
              updateValue={(value) =>
                setFormData((prev) => ({ ...prev, email: value }))
              }
            />

            <TextInput
              label={_("user", "phone_number_label")}
              placeholder={_("user", "placeholder_phone_number")}
              labelOutside={true}
              initialValue={formData.phoneNumber}
              updateValue={(value) =>
                setFormData((prev) => ({ ...prev, phoneNumber: value }))
              }
            />

            <TextInput
              label={_("user", "location_label")}
              placeholder={_("user", "placeholder_location")}
              labelOutside={true}
              initialValue={formData.location}
              updateValue={(value) =>
                setFormData((prev) => ({ ...prev, location: value }))
              }
            />
          </Box>
        </Box>
      </div>

      {/* Action Buttons */}
      <Box className={css.actionButtons}>
        <FormButton
          label={_("general", "btn_save")}
          onSubmit={handleSave}
          disabled={!formData.identifier.trim()}
          inline={true}
          variant="contained"
          color="primary"
        />
        <FormButton
          label={_("general", "btn_cancel")}
          onSubmit={handleCancel}
          inline={true}
          variant="outlined"
        />
      </Box>

      <ConfirmDialog
        open={deleteDialog}
        title={_("general", "btn_delete")}
        message={_("general", "confirm_delete")}
        confirmLabel={_("general", "btn_delete")}
        cancelLabel={_("general", "btn_cancel")}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteDialog(false)}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default EditUserScreen;
